import { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { Star, ChevronLeft, ChevronRight } from "lucide-react";
import { FadeUp, Eyebrow } from "./motion";
import { useContent } from "../lib/content";

export default function Testimonials() {
  const { testimonials } = useContent();
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: "start" });
  const [index, setIndex] = useState(0);

  const onSelect = useCallback(() => {
    if (emblaApi) setIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.on("select", onSelect);
    onSelect();
  }, [emblaApi, onSelect]);

  return (
    <section id="reviews" data-testid="reviews-section" className="py-20 sm:py-28 bg-sand-soft/60 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
        <FadeUp className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <Eyebrow className="mb-4">Our customers say</Eyebrow>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-slate-900 max-w-xl">
              Trusted across <span className="italic text-reef">Dubai.</span>
            </h2>
          </div>
          <div className="flex gap-2.5">
            <button
              data-testid="reviews-prev-btn"
              onClick={() => emblaApi?.scrollPrev()}
              className="p-3 rounded-full border border-reef/25 text-reef hover:bg-reef hover:text-sand-bg transition-colors"
              aria-label="Previous review"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              data-testid="reviews-next-btn"
              onClick={() => emblaApi?.scrollNext()}
              className="p-3 rounded-full border border-reef/25 text-reef hover:bg-reef hover:text-sand-bg transition-colors"
              aria-label="Next review"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </FadeUp>

        <FadeUp delay={0.1}>
          <div className="mt-12 overflow-hidden" ref={emblaRef} data-testid="reviews-carousel">
            <div className="flex gap-5">
              {testimonials.map((r, i) => (
                <div key={i} className="flex-[0_0_100%] sm:flex-[0_0_48%] lg:flex-[0_0_32%] min-w-0">
                  <div
                    data-testid={`review-card-${i}`}
                    className="h-full bg-white border border-sand-border rounded-2xl p-7 flex flex-col"
                  >
                    <div className="flex gap-1">
                      {Array.from({ length: 5 }).map((_, s) => (
                        <Star key={s} className="w-4 h-4 text-reef fill-reef" />
                      ))}
                    </div>
                    <p className="mt-4 font-serif italic text-lg text-slate-700 leading-relaxed flex-1">
                      “{r.text}”
                    </p>
                    <div className="mt-6 pt-5 border-t border-sand-border flex items-center gap-3">
                      <span className="w-10 h-10 rounded-full bg-reef-light text-reef font-serif text-lg flex items-center justify-center">
                        {(r.name || "R")[0]}
                      </span>
                      <div>
                        <p className="text-sm font-semibold text-slate-900">{r.name}</p>
                        <p className="font-mono text-[10px] tracking-[0.15em] uppercase text-slate-400">{r.location}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="mt-7 flex justify-center gap-2">
            {testimonials.map((_, i) => (
              <span
                key={i}
                className={`h-1.5 rounded-full transition-all duration-300 ${i === index ? "w-7 bg-reef" : "w-1.5 bg-reef/25"}`}
              />
            ))}
          </div>
        </FadeUp>
      </div>
    </section>
  );
}
